import React, { useEffect, useRef, useState } from "react";
import { InputNumber } from "primereact/inputnumber";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/20/solid";

type SaveStatus = "idle" | "saving" | "saved" | "error";

type CommitResult = boolean | "cancel";

interface ReceivingGroupInlineInputProps {
  ariaLabel: string;
  value: string | number | null | undefined;
  onCommit: (value: number) => Promise<CommitResult>;
  currency?: boolean;
  suffix?: string;
  commitOnBlur?: boolean;
}

function toNumber(value: string | number | null | undefined): number | null {
  if (value == null || value === "") return null;
  const next = typeof value === "number" ? value : Number(value);
  return Number.isFinite(next) ? next : null;
}

export default function ReceivingGroupInlineInput({
  ariaLabel,
  value,
  onCommit,
  currency = false,
  suffix,
  commitOnBlur = false,
}: ReceivingGroupInlineInputProps) {
  const normalizedValue = toNumber(value);
  const [draft, setDraft] = useState<number | null>(normalizedValue);
  const [status, setStatus] = useState<SaveStatus>("idle");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftRef = useRef<number | null>(draft);
  const committedValueRef = useRef<number | null>(normalizedValue);
  const committingRef = useRef(false);

  useEffect(() => {
    setDraft(normalizedValue);
    draftRef.current = normalizedValue;
    committedValueRef.current = normalizedValue;
  }, [normalizedValue]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    },
    [],
  );

  const commit = async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const nextValue = draftRef.current;
    if (committingRef.current) return;
    if (nextValue == null || !Number.isFinite(nextValue) || nextValue < 0) {
      setStatus("error");
      return;
    }
    if (nextValue === committedValueRef.current) return;

    committingRef.current = true;
    setStatus("saving");
    try {
      const result = await onCommit(nextValue);
      if (result === "cancel") {
        draftRef.current = committedValueRef.current;
        setDraft(committedValueRef.current);
        setStatus("idle");
        return;
      }
      setStatus(result ? "saved" : "error");
      if (result) {
        committedValueRef.current = nextValue;
        if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
        savedTimerRef.current = setTimeout(() => setStatus("idle"), 1400);
      }
    } finally {
      committingRef.current = false;
    }
  };

  const scheduleCommit = (nextDraft: number | null) => {
    draftRef.current = nextDraft;
    setDraft(nextDraft);
    setStatus("idle");
    if (timerRef.current) clearTimeout(timerRef.current);
    if (commitOnBlur) return;
    timerRef.current = setTimeout(() => void commit(), 450);
  };

  const unitSuffix = suffix?.trim() ? ` ${suffix.trim()}` : undefined;

  const statusIcon =
    status === "saving" ? (
      <span
        className="block h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#CBD5E1] border-t-[#2563EB]"
        aria-label="Saving"
      />
    ) : status === "saved" ? (
      <CheckCircleIcon className="h-4 w-4 text-[#16A34A]" aria-label="Saved" />
    ) : status === "error" ? (
      <ExclamationCircleIcon
        className="h-4 w-4 text-[#DC2626]"
        aria-label="Save failed"
      />
    ) : null;

  return (
    <div
      className="flex w-full min-w-0 items-center justify-end"
      onClick={(event) => event.stopPropagation()}
    >
      {statusIcon ? (
        <span className="mr-1.5 w-4 shrink-0" aria-live="polite">
          {statusIcon}
        </span>
      ) : null}
      <InputNumber
        value={draft}
        onValueChange={(event) => scheduleCommit(event.value ?? null)}
        onBlur={() => void commit()}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === "Enter") {
            event.currentTarget.blur();
          }
          if (event.key === "Escape") {
            event.preventDefault();
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = null;
            draftRef.current = normalizedValue;
            setDraft(normalizedValue);
            setStatus("idle");
            event.currentTarget.blur();
          }
        }}
        min={0}
        prefix={currency ? "$" : undefined}
        suffix={unitSuffix}
        minFractionDigits={currency ? 2 : 0}
        maxFractionDigits={4}
        useGrouping={currency}
        aria-label={ariaLabel}
        inputClassName="box-border w-full min-w-0 rounded-lg border border-[#CBD5E1] bg-white px-2.5 py-1.5 text-right text-sm tabular-nums text-[#0F172A] shadow-sm"
        className="receiving-inline-number min-w-0"
      />
    </div>
  );
}
